import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
} from '@mui/material'
import { HourglassEmpty } from '@mui/icons-material'
import { serverNow } from '@api/serverClock'

interface CooldownIndicatorProps {
  cooldownEndsAt: string
  waitingReason?: string | null
  onCooldownEnd?: () => void
}

export default function CooldownIndicator({
  cooldownEndsAt,
  waitingReason,
  onCooldownEnd,
}: CooldownIndicatorProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [totalSeconds, setTotalSeconds] = useState(0)
  const hasCalledEndRef = useRef(false)
  const onCooldownEndRef = useRef(onCooldownEnd)

  // Keep callback ref updated
  useEffect(() => {
    onCooldownEndRef.current = onCooldownEnd
  }, [onCooldownEnd])

  useEffect(() => {
    // Reset the called flag when cooldownEndsAt changes
    hasCalledEndRef.current = false

    // Ensure the timestamp is treated as UTC (append Z if missing)
    const utcTimestamp = cooldownEndsAt.endsWith('Z') ? cooldownEndsAt : `${cooldownEndsAt}Z`
    const endTime = new Date(utcTimestamp).getTime()
    // Convert server-side endTime into a LOCAL wall-clock deadline by adding the
    // server→browser offset. After this, we only compare against Date.now() and
    // the countdown tracks the user's display clock exactly, even if their machine
    // is drifted from the server.
    const localEndTime = endTime + (Date.now() - serverNow())
    const initialRemaining = Math.max(0, Math.ceil((localEndTime - Date.now()) / 1000))

    setRemainingSeconds(initialRemaining)
    setTotalSeconds(initialRemaining)

    if (initialRemaining <= 0) {
      if (!hasCalledEndRef.current) {
        hasCalledEndRef.current = true
        onCooldownEndRef.current?.()
      }
      return
    }

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((localEndTime - Date.now()) / 1000))
      setRemainingSeconds(remaining)

      if (remaining <= 0) {
        clearInterval(interval)
        if (!hasCalledEndRef.current) {
          hasCalledEndRef.current = true
          onCooldownEndRef.current?.()
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [cooldownEndsAt])

  const progress = totalSeconds > 0
    ? ((totalSeconds - remainingSeconds) / totalSeconds) * 100
    : 100

  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`
    }
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 100,
        left: '50%',
        transform: 'translateX(-50%)',
        p: 2,
        minWidth: 280,
        maxWidth: 400,
        borderRadius: 2,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        zIndex: 1200,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <HourglassEmpty
          sx={{
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '50%': { transform: 'rotate(180deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography variant="subtitle1" fontWeight="bold">
          On Cooldown
        </Typography>
        <Typography variant="h6" sx={{ ml: 'auto' }}>
          {formatTime(remainingSeconds)}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(0,0,0,0.2)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: 'warning.contrastText',
          },
        }}
      />

      {waitingReason && (
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
          {waitingReason}
        </Typography>
      )}
    </Paper>
  )
}
